import { useEffect, useRef, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import UserService from "../../services/UserService";
import AuthContext from "../../auth/AuthContext";
import AlertService from "../../services/AlertService";
import Fqicon from "../../assets/img/Fq.png";

const EmailOtpVerify = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const OTP_LENGTH = 8;
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);

  const inputRefs = useRef([]);

  /* AUTO FOCUS */
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (value, index) => {
    if (!/^[a-zA-Z0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };


  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(0, OTP_LENGTH);

    if (!pasted) return;

    const newOtp = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((char, i) => {
      newOtp[i] = char;
    });

    setOtp(newOtp);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };


  const handleVerify = async (e) => {
    e.preventDefault();
    const finalOtp = otp.join("");

    if (finalOtp.length < OTP_LENGTH) {
      AlertService.error("Please enter the complete OTP");
      return;
    }

    try {
      setLoadingVerify(true);
      const result = await UserService.ValidateOTP(finalOtp, authContext);

      if (result.isSuccess) {
        authContext.setMFAVerified(true);
        navigate("/");
      } else {
        AlertService.error(result.errorMessage);
      }
    } catch (err) {
      console.error(err);
      AlertService.error("OTP verification failed");
    } finally {
      setLoadingVerify(false);
    }
  };


  const handleResendOTP = async () => {
    try {
      setLoadingResend(true);
      const result = await UserService.SendOTP(authContext);

      if (result.isSuccess) {
        AlertService.success("A new OTP has been sent to your email");
        setOtp(Array(OTP_LENGTH).fill(""));
        inputRefs.current[0]?.focus();
      } else {
        AlertService.error(result.errorMessage);
      }
    } catch (err) {
      console.error(err);
      AlertService.error("Failed to resend OTP");
    } finally {
      setLoadingResend(false);
    }
  };

  return (
    <div className="min-h-screen flex font-sans">

      {/* LEFT PANEL */}
      <div className="hidden md:flex w-1/2 bg-[#0e3a46] relative overflow-hidden">
        <div className="absolute top-12 -right-10 bg-white h-24 w-56 rounded-full flex items-center pl-12 shadow-md">
          <img src={Fqicon} alt="Logo" className="h-12" />
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex w-full md:w-1/2 items-center justify-center bg-white px-4">
        <form
          onSubmit={handleVerify}
          className="w-full max-w-[420px] text-center"
        >
          <h2 className="text-3xl font-semibold mb-2">
            OTP Verification
          </h2>

          <p className="text-base text-gray-600 mb-6">
            Please enter the One-Time Password sent to your email
          </p>

          {/* OTP INPUTS */}
          <div
            className="flex justify-center gap-2 sm:gap-3 mb-8"
            onPaste={handlePaste}
          >
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                value={digit}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                maxLength={1}
                className="
                  w-10 h-10 sm:w-12 sm:h-12
                  border border-gray-300
                  rounded-md
                  text-center
                  text-base sm:text-lg
                  outline-none
                  focus:ring-2
                  focus:ring-[#0e3a46]
                  transition
                "
              />
            ))}
          </div>

          {/* BUTTONS */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={loadingResend}
              className="
                flex-1 h-11
                border border-gray-400
                rounded-md
                text-gray-700
                hover:bg-gray-50
                focus:ring-2 focus:ring-[#0e3a46]
              "
            >
              {loadingResend ? "Resending..." : "Resend OTP"}
            </button>

            <button
              type="submit"
              disabled={loadingVerify}
              className="
                flex-1 h-11
                rounded-md
                text-white font-medium
                bg-[#0e3a46]
                hover:opacity-95
                focus:ring-2 focus:ring-[#0e3a46]
              "
            >
              {loadingVerify ? "Verifying..." : "Verify OTP"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailOtpVerify;